import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Mail, Phone, Building2 } from "lucide-react";
import WhatsAppLink from "@/components/dashboard/WhatsAppLink";
import { RowActions } from "@/components/dashboard/RowActions";
import { toast } from "sonner";
import usePagination from "@/lib/usePagination";
import TablePagination from "@/components/dashboard/TablePagination";

export default function Messages() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null);
  const { page, setPage, totalPages, pageItems, rangeStart, rangeEnd, total } = usePagination(rows, 20);
  const load = async () => setRows((await api.get("/contact")).data);
  useEffect(() => { load(); }, []);
  const del = async (id) => {
    try { await api.delete(`/contact/${id}`); toast.success("Message deleted"); load(); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="space-y-8" data-testid="messages-page">
      <div>
        <div className="label-eyebrow text-navy/50">Admin</div>
        <h1 className="serif-display text-4xl mt-2">Contact form messages</h1>
        <p className="text-navy/60 mt-2">{rows.length} enquiries</p>
      </div>
      <div className="bg-white border border-navy/10">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Company</TableHead>
              <TableHead>Subject</TableHead>
              <TableHead>Received</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-navy/50 py-8">No messages yet.</TableCell></TableRow>}
            {pageItems.map((m) => (
              <TableRow key={m.message_id} data-testid={`message-row-${m.message_id}`}>
                <TableCell className="serif-display cursor-pointer hover:text-gold" onClick={()=>setOpen(m)}>{m.name}</TableCell>
                <TableCell className="text-navy/80">{m.email}</TableCell>
                <TableCell>{m.company || "—"}</TableCell>
                <TableCell className="max-w-xs truncate cursor-pointer" onClick={()=>setOpen(m)}>{m.subject}</TableCell>
                <TableCell className="text-navy/60 text-sm">{new Date(m.created_at).toLocaleString()}</TableCell>
                <TableCell>
                  <RowActions
                    onEdit={() => setOpen(m)}
                    onDelete={() => del(m.message_id)}
                    editTestid={`view-message-${m.message_id}`}
                    deleteTestid={`delete-message-${m.message_id}`}
                    itemLabel="this message"
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination page={page} totalPages={totalPages} rangeStart={rangeStart} rangeEnd={rangeEnd} total={total} onPrev={() => setPage((p) => Math.max(1, p - 1))} onNext={() => setPage((p) => Math.min(totalPages, p + 1))} testidPrefix="messages-table" />
      </div>

      <Dialog open={!!open} onOpenChange={(o)=>!o && setOpen(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="serif-display text-2xl">{open?.subject}</DialogTitle>
            <DialogDescription>from {open?.name} · {new Date(open?.created_at || Date.now()).toLocaleString()}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-4 text-sm border-y border-navy/10 py-4">
            <div className="flex items-center gap-2"><Mail size={14} className="text-gold"/> {open?.email}</div>
            <div className="flex items-center gap-2"><Phone size={14} className="text-gold"/> {open?.phone || "—"}</div>
            <div className="flex items-center gap-2"><Building2 size={14} className="text-gold"/> {open?.company || "—"}</div>
          </div>
          <div className="whitespace-pre-wrap text-navy/80 leading-relaxed">{open?.message}</div>
          <DialogFooter>
            <a href={`mailto:${open?.email}?subject=Re: ${encodeURIComponent(open?.subject || '')}`} className="btn-primary" data-testid="msg-reply-email">Reply via email</a>
            {open?.phone && (
              <WhatsAppLink
                phone={open.phone}
                message={`Hi ${(open?.name || "").split(" ")[0] || "there"}, this is Ravemora — thanks for reaching out about "${open?.subject || ''}".`}
                label="WhatsApp"
                testid="msg-reply-whatsapp"
                className="btn-outline"
              />
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
